/**
 * 路由初始化模块
 *
 * 在应用启动时初始化路由，确保页面刷新后路由能正确加载
 */

import router from './index'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { useUserStoreWithOut } from '@/store/modules/user'
import type { RouteRecordRaw } from 'vue-router'
import { generateAdminRoleRoutesFromServer } from '@/utils/setupAdminRouterAccess'
import { firstAccessibleMenuPath } from '@/utils/adminRouteFilter'
import { revokeAdminSessionAndRedirectToLogin } from '@/utils/revokeAdminSession'

/**
 * 初始化路由
 *
 * 在应用启动时调用，如果用户已登录，立即注册路由
 * 这样可以避免页面刷新后首次访问时的404问题
 */
export async function initRoutes() {
  const permissionStore = usePermissionStoreWithOut()
  const userStore = useUserStoreWithOut()

  console.log('[Router Init] Starting route initialization...')
  console.log('[Router Init] User info:', userStore.getUserInfo ? 'exists' : 'null')
  console.log('[Router Init] Is add routers:', permissionStore.getIsAddRouters)

  // 如果用户已登录且路由未添加，立即注册路由
  if (userStore.getUserInfo && !permissionStore.getIsAddRouters) {
    console.log('[Router Init] User is logged in, initializing routes...')

    try {
      // 强制使用静态路由模式（刷新时确保所有路由都可用）
      // 即使 appStore 配置为动态路由，刷新时也使用静态路由
      console.log('[Router Init] Using admin role + menu-access filtered routes')
      const { grants, userRoles } = await generateAdminRoleRoutesFromServer()
      const mainRoutes = permissionStore.addRouters.filter((r) => r.name !== '404Page')
      if (!firstAccessibleMenuPath(mainRoutes, userRoles, grants)) {
        console.warn('[Router Init] No accessible admin menu for current user; clearing session')
        sessionStorage.setItem('admin_flash_no_menu_access', '1')
        await revokeAdminSessionAndRedirectToLogin()
        return
      }

      // 注册路由到 router 实例
      const routes = permissionStore.addRouters
      console.log(
        '[Router Init] Generated routes:',
        routes.map((r) => r.path)
      )

      routes.forEach((route) => {
        router.addRoute(route as unknown as RouteRecordRaw)
      })

      // 标记路由已添加
      permissionStore.setIsAddRouters(true)

      console.log('[Router Init] Routes initialized successfully:', routes.length, 'routes added')
      console.log(
        '[Router Init] All registered routes:',
        router.getRoutes().map((r) => ({ path: r.path, name: r.name }))
      )

      // 如果当前 URL 需要重新解析（从 hash 中获取路径）
      const currentHash = window.location.hash
      if (currentHash && currentHash.startsWith('#/')) {
        const currentPath = currentHash.substring(1) // 移除 #
        console.log('[Router Init] Current hash path:', currentPath)

        // 检查当前路径是否能匹配到路由
        const matched = router.resolve(currentPath)
        console.log(
          '[Router Init] Route match result:',
          matched.matched.length > 0 ? '✅ Found' : '❌ Not found'
        )

        if (matched.matched.length > 0) {
          console.log('[Router Init] Route matched, will navigate after mount')
        } else {
          console.log('[Router Init] ⚠️ Route not matched, check route configuration')
        }
      }
    } catch (error) {
      console.error('[Router Init] Failed to initialize routes:', error)
      throw error
    }
  } else if (!userStore.getUserInfo) {
    console.log('[Router Init] No user info, skipping route initialization')
  } else {
    console.log('[Router Init] Routes already initialized, skipping')
  }
}
